/**
 * html2canvas 不支持 filter 和 backdropFilter属性
 * dom-to-image 支持 filter， 不支持 backdropFilter 属性
 *
 * 已知问题：
 * - dom-to-image [Image is often missing on first render on Safari iOS](https://github.com/tsayen/dom-to-image/issues/343)
 */
import React, { useCallback } from 'react'
import { MyModal } from '~/components/ui/MyModal'
import { CDNImage } from '~/components/ui/CDNImage'
import styled from 'styled-components'
import { QRCodeSVG } from 'qrcode.react'
import { CopyText } from '~/components/business/CopyText'
import AppLaunch from '~/components/icon/AppLaunch'
import {
  ActionType,
  ArtworkDBType,
  countShareProduct,
  countShareProductVariables,
  getOwnedProductList_itemModule_productList_edges_node,
  getProductDetail_itemModule_artwork,
  RareFlag,
} from '~/graphqls/types'
import { FenToYuan, toSmallImage } from '~/utils'
import domtoimage from 'dom-to-image'
import { message } from 'antd'
import { MyRare } from '~/components/ui/MyRare'
import useProductPrice from '~/hooks/useProductPrice'
import { useMutation } from '@apollo/client'
import { COUNT_SHARE_PRODUCT } from '~/graphqls/gqls/creator-center'
import { getProductPrice } from '~/components/pages/product/common'

const StyledAction = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px auto 0;
  font-size: 12px;
  line-height: 20px;
  img {
    margin-bottom: 8px;
  }
  .action-item {
    width: 54px;
    cursor: pointer;
    text-align: center;
    & + .action-item {
      margin-left: 36px;
    }
  }
`
const StyleCertificateNo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  margin-top: 12px;
  .left {
    height: 24px;
    padding: 0px 4px;
    background-color: #f9deb9;
    border-radius: 4px 0px 0px 4px;
    color: ${props => props.theme['neutral-50']};
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .right {
    position: relative;
    color: #f9deb9;
    width: 281px;
    height: 24px;
    line-height: 24px;
    text-align: center;
    &::before {
      position: absolute;
      bottom: 0;
      width: 200%;
      height: 200%;
      content: '';
      display: block;
      border: 1px solid #f9deb9;
      border-radius: 0px 8px 8px 0px;
      transform: scale(0.5);
      transform-origin: left bottom;
    }
  }
`
const CustomMyModal = styled(MyModal)`
  .ant-modal-body {
    padding: 32px 36px;
  }
`

const Footer = ({ url }: { url: string }) => {
  return (
    <div className="flex items-center justify-between bg-neutral-100 p-4">
      <div className="flex items-center">
        <AppLaunch />
        <div className="ml-2">
          <div className="text-neutral-800 text-sm">无界版图</div>
          <div className="text-neutral-600 text-xs">把数字版权装进口袋</div>
        </div>
      </div>

      <div className="flex items-center">
        <div className="text-neutral-600 w-[16px] text-[12px] leading-[14px]">
          扫码查看
        </div>
        <div>
          <QRCodeSVG
            value={url}
            size={53}
            style={{
              border: '1px solid #e6e8ec',
              padding: 1,
              display: 'block',
            }}
          ></QRCodeSVG>
        </div>
      </div>
    </div>
  )
}

export const ProductShareModal = ({
  visible,
  setVisible,
  product,
  shareDoc,
}: {
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  product:
    | getProductDetail_itemModule_artwork
    | getOwnedProductList_itemModule_productList_edges_node
  shareDoc?: string
}) => {
  const isAiCopyRight = product.type === ArtworkDBType.AI_COPYRIGHT
  const { buyPrice } = useProductPrice({ buy: product.buy })
  const aiPrice = getProductPrice(product)
  const _buyPrice = isAiCopyRight ? aiPrice : buyPrice
  console.log(aiPrice)
  /**
   * 统计作品分享量
   */
  const [countShareProductAction] = useMutation<
    countShareProduct,
    countShareProductVariables
  >(COUNT_SHARE_PRODUCT)

  /** 是否是存证 */
  const isCertificate = product?.type === ArtworkDBType.EVIDENCE
  const SHARE_URL = `${process.env.NEXT_PUBLIC_MOBIE_SITE}/product/${product.id}`
  const ID = 'share-product'
  let resolver: (arg0: boolean) => void
  const promise = new Promise<boolean>(resolve => {
    resolver = resolve
  })

  const countShareProductHandler = useCallback(() => {
    countShareProductAction({
      variables: {
        param: {
          actionType: ActionType.CONFIRM,
          artworkId: product.id,
        },
      },
    })
  }, [product.id])

  const onDownload = useCallback(async () => {
    countShareProductHandler()

    try {
      // 等待图片加载完成
      await promise
      // 这里故意转两遍，解决 https://github.com/tsayen/dom-to-image/issues/343
      await domtoimage.toJpeg(document.getElementById(ID) as HTMLElement)
      const imgUrl = await domtoimage.toJpeg(
        document.getElementById(ID) as HTMLElement
      )
      let aEl = document.createElement('a')
      aEl.download = `作品分享`
      aEl.href = imgUrl
      document.body.appendChild(aEl)
      aEl.click()
      aEl.remove()
    } catch (err) {
      message.error('请重试')
    }
  }, [countShareProductHandler])

  return (
    <CustomMyModal
      visible={visible}
      title="分享作品"
      onCancel={() => setVisible(false)}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
      width={448}
      footer={null}
    >
      <div id={ID}>
        <div className="p-4 relative overflow-hidden">
          {/* bg */}
          <div
            className="absolute -top-4 -left-4 -right-4 -bottom-4"
            style={{ filter: 'blur(20px) brightness(0.5)' }}
          >
            <img
              className="w-[110%] h-[110%]"
              src={toSmallImage(product.artworkUrl || '')}
              alt=""
            />
          </div>
          {/* backdropFilter 效果最好，但是html2canvas和domtoimage都不支持 */}
          {/* <div
                className="absolute top-0 left-0 right-0 bottom-0"
                style={{ backdropFilter: 'blur(20px) brightness(0.5)' }}
              ></div> */}

          <div className="relative">
            <div className="w-[343px] h-[343px] rounded-2xl bg-neutral-50/30 p-3 flex items-center justify-center">
              <img
                className="rounded-xl max-h-[319px] max-w-[319px]"
                src={toSmallImage(product.artworkUrl || '')}
                alt=""
                onLoad={() => {
                  resolver(true)
                }}
              />
            </div>

            <div className="text-lg text-neutral-800 mt-4 overflow-hidden text-ellipsis whitespace-nowrap">
              {product.name}{' '}
              <MyRare className="-mt-1" rare={product?.rareFlag as RareFlag} />
            </div>
            <div className="flex items-center justify-between text-neutral-600 mt-3">
              <div className="overflow-hidden">
                <div>
                  <span className="text-xs">创作者：</span>
                  <span className="text-sm font-medium">
                    {product.creator?.name ?? ''}
                  </span>
                </div>
                {/* 先不要 */}
                {/* {!shareDoc ? (
                  <div>
                    <span className="text-xs">版权所有者：</span>
                    <span className="text-sm font-medium">
                      {(product as getProductDetail_itemModule_artwork).owner
                        ?.name ?? ''}
                    </span>
                  </div>
                ) : (
                  <div className="text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                    {shareDoc}
                  </div>
                )} */}
              </div>
              {!isCertificate && (
                <div className="ml-1 shrink-0">
                  <div className="text-[#FFD166] text-base flex justify-end items-baseline">
                    {_buyPrice.isSame ? (
                      <>
                        <span className="text-xs">¥</span>
                        <span className="text-lg whitespace-nowrap">{`${FenToYuan(
                          _buyPrice.max
                        )}`}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xs">¥</span>
                        <span className="text-lg whitespace-nowrap">{`${FenToYuan(
                          _buyPrice.min
                        )}`}</span>{' '}
                        - <span className="text-xs">¥</span>
                        <span className="text-lg whitespace-nowrap">{`${FenToYuan(
                          _buyPrice.max
                        )}`}</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500">价格具有时效性</div>
                </div>
              )}
            </div>
            {product?.chain?.certificateNo && (
              <StyleCertificateNo>
                <div className="left">
                  <CDNImage
                    src="/images/product/certificate-success-icon.svg"
                    className="w-4 h-4 mr-[2px]"
                  />
                  已存证
                </div>
                <div className="right">
                  保全号：{product?.chain?.certificateNo}
                </div>
              </StyleCertificateNo>
            )}
          </div>
        </div>
        <Footer url={SHARE_URL} />
      </div>

      <StyledAction>
        <div className="action-item">
          <CopyText
            text={`${SHARE_URL}`}
            successCallback={countShareProductHandler}
          >
            <img src="/images/invite/link.svg" alt="" />
            复制链接
          </CopyText>
        </div>
        <div className="action-item" onClick={onDownload}>
          <img src="/images/invite/download.svg" alt="" />
          保存图片
        </div>
      </StyledAction>
    </CustomMyModal>
  )
}
